// The app-stage badge (Flow 5; docs/phase-0/slice5-ux-flows.md §8.4;
// docs/phase-0/slice5-component-designs.md §3.5 `.stage-badge`). A plain-text label
// (never color-only - stage is not a pass/fail signal) plus a keyboard-operable
// disclosure stating what the current stage unlocks. Lighter than `GdprRoutingPanel`'s
// full focus trap (this popover does not block the page): focus moves to the trigger
// only (it already has focus from the click), Escape or an outside click closes it,
// and it never Tab-traps.
import { useEffect, useId, useRef, useState } from "react";
import type { AppStage } from "@nightcrew/shared";
import { buildCopy, type BuildCopyKey } from "../copy/build.js";

const STAGE_LABEL_KEY: Record<AppStage, BuildCopyKey> = {
  validated: "build.stage.label.validated",
  mvp: "build.stage.label.mvp",
  production: "build.stage.label.production",
};

const STAGE_UNLOCK_KEY: Record<AppStage, BuildCopyKey> = {
  validated: "build.stage.unlock.validated",
  mvp: "build.stage.unlock.mvp",
  production: "build.stage.unlock.production",
};

export const StageBadge = ({ stage }: { readonly stage: AppStage }): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) === true) return;
      if (triggerRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  return (
    <span className="stage-badge-wrap" data-testid="stage-badge-wrap">
      <span className="chip stage-badge" data-testid="stage-badge" data-stage={stage}>
        {buildCopy(STAGE_LABEL_KEY[stage])}
      </span>
      <button
        type="button"
        className="turn-lang-toggle"
        ref={triggerRef}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        {buildCopy("build.stage.unlockTrigger")}
      </button>
      {open ? (
        <div id={popoverId} className="card pad stage-unlock-popover" ref={popoverRef}>
          <p style={{ margin: 0, fontSize: 13 }}>{buildCopy(STAGE_UNLOCK_KEY[stage])}</p>
        </div>
      ) : null}
    </span>
  );
};
